// ModeComparison.js — Side-by-side comparison of all 4 profit-taking modes
import React, { useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { runProjection } from '../../lib/projections/engine';
import { PROFIT_TAKING_MODES, COST_DEFAULTS } from '../../lib/projections/constants';

/**
 * Format as compact currency.
 */
function fmtVal(v, ccy) {
  if (v === null || v === undefined) return '--';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  const sym = ccy === 'GBP' ? '\u00a3' : ccy === 'EUR' ? '\u20ac' : '$';
  if (abs >= 1e6) return `${sign}${sym}${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}${sym}${(abs / 1e3).toFixed(0)}K`;
  return `${sign}${sym}${abs.toFixed(0)}`;
}

function fmtPct(v) {
  if (v === null || v === undefined) return '--';
  return `${(v * 100).toFixed(2)}%`;
}

/**
 * Mode comparison running the engine 4 times for each profit-taking mode.
 * @param {{ positions, currentFxRates, currentYields, inputs, baseCurrency }} props
 */
const ModeComparison = ({ positions, currentFxRates, currentYields, inputs, baseCurrency = 'USD' }) => {
  const [selectedScenario, setSelectedScenario] = useState('base');

  const modeResults = useMemo(() => {
    if (!positions || positions.length === 0) return [];

    return Object.keys(PROFIT_TAKING_MODES).map(modeKey => {
      const result = runProjection({
        positions,
        currentFxRates,
        currentYields,
        baseCurrency: inputs.baseCurrency,
        mode: modeKey,
        modeParams: { withdrawalPct: (inputs.withdrawalPct || 5) / 100 },
        hedgeRatio: inputs.hedgeRatio,
        costs: inputs.costs || COST_DEFAULTS,
        tenorDays: inputs.tenorDays,
        scenarios: [selectedScenario],
        adverseShockYear: inputs.adverseShockYear,
      });

      const scenario = result.scenarios[selectedScenario];
      if (!scenario) return null;

      const totalIncome = scenario.years.reduce((s, y) => s + (y.income || 0), 0);
      const totalWithdrawn = scenario.years.reduce((s, y) => s + (y.withdrawal || 0), 0);

      return {
        mode: modeKey,
        label: PROFIT_TAKING_MODES[modeKey].label,
        endValue: scenario.endValueHedged,
        annualized: scenario.annualizedReturnHedged,
        totalIncome,
        totalWithdrawn,
      };
    }).filter(Boolean);
  }, [positions, currentFxRates, currentYields, inputs, selectedScenario, baseCurrency]);

  if (!modeResults || modeResults.length === 0) return null;

  const chartData = modeResults.map(r => ({
    name: r.label,
    endValue: r.endValue,
  }));

  const BAR_COLORS = ['#3832e3', '#7b7eff', '#a1a1aa', '#e0e7ff'];

  return (
    <div className="bg-white border border-zinc-200 rounded-none p-5 mb-4">
      <div className="flex items-center justify-between mb-4">
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
          Mode Comparison
        </span>
        <select
          value={selectedScenario}
          onChange={e => setSelectedScenario(e.target.value)}
          className="font-mono text-[10px] px-2 py-1 border border-zinc-200 rounded-none bg-white text-zinc-700"
        >
          {(inputs.scenarios || ['base']).map(s => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
      </div>

      {/* Bar chart */}
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
          <XAxis dataKey="name" tick={{ fontSize: 9, fontFamily: 'monospace', fill: '#71717a' }} />
          <YAxis tick={{ fontSize: 9, fontFamily: 'monospace', fill: '#71717a' }} tickFormatter={v => fmtVal(v, baseCurrency)} width={65} />
          <Tooltip
            formatter={(value) => [fmtVal(value, baseCurrency), 'End Value']}
            labelStyle={{ fontFamily: 'monospace', fontSize: 10 }}
            contentStyle={{ fontFamily: 'monospace', fontSize: 10, border: '1px solid #e4e4e7', borderRadius: 0 }}
          />
          <Bar dataKey="endValue" fill="#3832e3" name="End Value (Hedged)" />
        </BarChart>
      </ResponsiveContainer>

      {/* Summary table */}
      <div className="overflow-x-auto mt-4">
        <table className="w-full text-left" aria-label="Mode comparison summary">
          <thead>
            <tr className="border-b border-zinc-200">
              {['Mode', 'End Value', 'Ann. Return', 'Total Income', 'Total Withdrawn'].map(h => (
                <th key={h} className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 py-2 px-2 font-medium">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {modeResults.map(r => (
              <tr key={r.mode} className="border-b border-zinc-100 hover:bg-zinc-50">
                <td className="font-mono text-[11px] text-zinc-700 py-2 px-2">{r.label}</td>
                <td className="font-mono text-[11px] text-zinc-700 py-2 px-2 tabular-nums">{fmtVal(r.endValue, baseCurrency)}</td>
                <td className="font-mono text-[11px] text-zinc-700 py-2 px-2 tabular-nums">{fmtPct(r.annualized)}</td>
                <td className="font-mono text-[11px] text-zinc-700 py-2 px-2 tabular-nums">{fmtVal(r.totalIncome, baseCurrency)}</td>
                <td className="font-mono text-[11px] text-zinc-700 py-2 px-2 tabular-nums">{fmtVal(r.totalWithdrawn, baseCurrency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ModeComparison;
